<template>
  <div :class="`p-4 site-list ${this.$store.state.viewMode.sites}`">
    <c-row class="mb-3">
      <c-col class="view-as">
        View as
        <a href="#" @click="e => setViewMode(e, 'list')">List</a>
        |
        <a href="#" @click="e => setViewMode(e, 'grid')">Grid</a>
      </c-col>
      <c-col>
        <add-site-button v-if="!hideAddSiteButton" class="float-right ml-2" />
        <c-button
            color="primary"
            size="sm"
            class="float-right"
            @click="showOnlyRequireUpdates = !showOnlyRequireUpdates"
        >
          {{ !showOnlyRequireUpdates ? 'Only show sites requiring updates' : 'Show all sites' }}
        </c-button>
      </c-col>
    </c-row>

    <template v-if="this.$store.state.viewMode.sites === 'list'">
      <c-row
        v-for="(site, index) in sitesSorted"
        v-show="(showOnlyRequireUpdates && site.updatesRequired > 0) || !showOnlyRequireUpdates"
        :key="index"
        class="py-2 site-row"
      >
        <c-col class="site-image" sm="1">
          <router-link
              :to="{ name: 'detailsSite', params: { siteId: site._id } }"
          >
            <img :src="getScreenShot(site)" class="screenshot" />
          </router-link>
        </c-col>
        <c-col class="site-name" sm="6">
          <router-link
            :to="{ name: 'detailsSite', params: { siteId: site._id } }"
            v-html="site.name || site.siteAddress"
          />
          <p class="mb-0 site-link">{{ site.siteAddress }}</p>
        </c-col>
        <c-col class="site-notes" sm="2">
          <span
            v-if="site.notes"
            v-c-popover="{
              header: 'Note',
              content: site.notes,
              placement: 'top-start',
              size: 'lg',
            }"
            class="mt-2 d-block"
          >
            <c-icon :content="freeSet.cilInfo" />
            Note
          </span>
        </c-col>
        <c-col class="site-actions" sm="3">
          <c-button
            v-if="site.updatesRequired"
            class="update-all-plugins float-right"
            color="kb-green"
            size="sm"
            @click="openConfirmUpdateAllPluginsModal(site)"
          >
            Update&nbsp;{{ site.updatesRequired }} plugin{{ site.updatesRequired > 1 ? 's' : '' }}
          </c-button>
        </c-col>
      </c-row>
    </template>

    <template v-else>
      <c-row class="py-2 site-row">
        <c-col sm="12" md="6" lg="4" xl="2" v-for="(site, index) in sitesSorted" :key="index" v-show="(showOnlyRequireUpdates && site.updatesRequired > 0) || !showOnlyRequireUpdates">
          <site-card :site="site" @updatePlugins="openConfirmUpdateAllPluginsModal(site)"/>
        </c-col>
      </c-row>
    </template>

    <confirm-modal
      title="Update All Plugins"
      :show.sync="showWpUpdateAllPluginsModal"
      @update:confirm="updateAllPlugins"
    >
      <div v-html="wpUpdateAllPluginsMessage" />
    </confirm-modal>
  </div>
</template>

<script>
import Vue from 'vue';
import { freeSet } from '@coreui/icons';
import _ from 'lodash';
import io from 'socket.io-client';
import { buildEndpoint } from '@/libs/buildEndPoint';
import { parseScrapeData } from '@/libs/plugin/parseScrapeData';
import ConfirmModal from '@/views/components/ConfirmModal';
import SiteCard from '@/views/components/site/Card';
import AddSiteButton from '@/views/components/site/add-site-button.vue';

const socket = io(buildEndpoint(''));

/**
 * Update site data for display
 * @param site
 * @param updatedData
 */
function updateSiteData(site, updatedData) {
  site.pluginCount = updatedData.pluginCount;
  site.scrapeData = updatedData.scrapeData;
  site.updatedAt = updatedData.updatedAt;
  site.wpVersion = updatedData.wpVersion;
  site.updatesRequired = updatedData.updatesRequired;
  // Update site name if found - it can change between scrapes
  if (updatedData.siteInfo.name !== undefined) {
    site.name = updatedData.siteInfo.name;
  }
}

export default {
  name: 'SiteList',
  components: {
    ConfirmModal,
    SiteCard,
    AddSiteButton,
  },
  props: {
    sites: Array,
    readOnly: Boolean,
    hideHeader: Boolean,
    noBox: Boolean,
    // showOnlyRequireUpdates: Boolean,
    // viewTypeDefault: String,
    md: String,
    lg: String,
    hideAddSiteButton: Boolean,
  },
  data() {
    return {
      showOnlyRequireUpdates: false,
      showWpUpdateAllPluginsModal: false,
      error: null,
      filterSites: null,
      freeSet,
      // viewType: 'list',
      wpUpdateAllPluginsMessage: '',
      siteToUpdate: null,
    };
  },
  computed: {
    sitesSorted() {
      if (this.showOnlyRequireUpdates) {
        return _.orderBy(this.sites, ['updatesRequired', 'name'], ['desc', 'asc']);
      }
      return this.sites;
    },
  },
  created() {
    // this.viewType = this.viewTypeDefault || 'list'
    const _this = this;
    socket.emit('room', Vue.localStorage.get('mp-sid'));
    socket.on('siteRefreshNotification', (data) => {
      const site = _.find(_this.sites, { _id: data.siteId });
      switch (data.action) {
        case 'site-refresh-started':
          site.isLoading = true;
          break;
        case 'site-refresh-finished':
          site.isLoading = false;
          site.pluginCount = data.data.pluginCount;
          site.wpVersion = data.data.wpVersion;
          site.updatesRequired = data.data.updatesRequired;
          site.updatedAt = data.data.updatedAt;
          site.success = data.data.success;
          break;
        default:
          break;
      }
    });
  },
  mounted() {},
  methods: {
    getScreenShot(site) {
      return _.get(site, 'screenshots.desktop.thumb.url', '/img/no-screenshot.svg');
    },
    clearSearch() {
      this.filterSites = null;
    },
    findSites(site) {
      const searchStr = site.name.toLowerCase() + site.siteAddress.toLowerCase();
      return searchStr.indexOf(this.filterSites) !== -1;
    },
    refreshSite(site) {
      site.isLoading = true;
      this.error = null;
      this.$http({
        url: buildEndpoint(`site/scrape/${site._id}`),
      })
        .then((response) => {
          site.isLoading = false;
          if (response.data.success) {
            const updatedData = parseScrapeData(response.data.data);
            updateSiteData(site, updatedData);
            site.scrapeDone = true;
          } else {
            this.error = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          site.isLoading = false;
          this.error = error;
        });
    },
    updateAllPlugins() {
      this.showWpUpdateAllPluginsModal = false;
      // this.queueSiteUpdate({
      //   siteId: this.siteToUpdate._id,
      //   siteName: this.siteToUpdate.name
      // })
      this.refreshSite(this.siteToUpdate);
      this.isUpdatingAllPlugins = true;
      this.pluginUpdateError = null;
      this.$http
        .get(buildEndpoint(`site/update-all-plugins/${this.siteToUpdate._id}`))
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('openSideBar');
          } else {
            this.pluginUpdateError = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.pluginUpdateError = error;
        });
    },
    openConfirmUpdateAllPluginsModal(site) {
      this.siteToUpdate = site;
      // const plural = site.pluginUpdateCount > 1 ? 's' : '';
      this.wpUpdateAllPluginsMessage = `This will update all plugins (${
        site.updatesRequired
      }) on <strong>${
        site.name
      }</strong>`
        + ' except for any plugins which you have blocked.'
        + '<BR><BR>'
        + 'Please check the notification drawer (right) for realtime progress.';
      this.showWpUpdateAllPluginsModal = true;
    },
    setViewMode(e, viewMode) {
      e.preventDefault();
      this.$store.commit('setViewModeSites', viewMode);
      // Save to user
      const data = {
        preferenceKey: 'viewModeSites',
        preferenceValue: viewMode,
      };
      this.$http.put(buildEndpoint('account/preferences/save'), data);
    },
  },
};
</script>

<style lang="scss" scoped>
.view-as {
  font-size: 14px;
  a {
    font-weight: bold;
    font-size: inherit;
  }
}

.c-dark-theme {
  .site-list {
    background-color: #23242d;
    &.list {
      .site-row {
        border-bottom: 1px solid #333;
      }
    }
    &.grid {
      .site-name {
        color: white;
      }
    }
  }
}

.site-list {
  font-size: 12px;
  &.list {
    .site-row {
      border-bottom: 1px solid #eee;
    }
    .site-name {
      font-weight: bold;
      .site-link {
        font-weight: normal;
      }
    }
    .site-notes {
      & > span {
        cursor: pointer;
      }
    }
    .screenshot {
      width: 50px;
      &.no-image {
        opacity: 0.5;
      }
    }
  }
  @media (min-width: 1200px) {
    .col-xl-2 {
      -webkit-box-flex: 0;
      -ms-flex: 0 0 33.3333%;
      flex: 0 0 33.3333%;
      max-width: 33.3333%;
    }
  }
  @media (min-width: 1400px) {
    .col-lg-4 {
      -webkit-box-flex: 0 !important;
      -ms-flex: 0 0 25% !important;
      flex: 0 0 25% !important;
      max-width: 25% !important;
    }
  }
  @media (min-width: 1800px) {
    .col-xl-2 {
      -webkit-box-flex: 0;
      -ms-flex: 0 0 25%;
      flex: 0 0 25%;
      max-width: 25%;
    }
  }
  //@media (min-width: 1600px) {
  //  .col-xl-2 {
  //    -webkit-box-flex: 0;
  //    -ms-flex: 0 0 16.66667%;
  //    flex: 0 0 16.66667%;
  //    max-width: 16.66667%;
  //  }
  //}
  @media (min-width: 2200px) {
    .col-xl-2 {
      -webkit-box-flex: 0;
      -ms-flex: 0 0 16.66667%;
      flex: 0 0 16.66667%;
      max-width: 16.66667%;
    }
  }
}
</style>
